/**
 * The lobby's Stardust chip (`#user-stardust`), kept honest after the lobby
 * is painted.
 *
 * `main.js` paints the chip once, from the profile that opens the lobby, and
 * nothing else in the lobby ever re-read it. Every Stardust spend happens on
 * another surface — a pack in the Collection, a craft in the Foundry, a Moon
 * Dream commit — and each of those surfaces keeps its own balance current
 * while the chip underneath stayed at the sign-in figure until a reload.
 * Measured on the real dev backend: 2575 ☆ in the lobby, one 100 ☆ pack in
 * the Collection (its header read 2475), back in the lobby: still 2575.
 *
 * Same shape as the Moonlight chip's `moonfall:moonlight-changed`: whoever
 * learns the authoritative balance announces it, and the chip listens. The
 * announcement carries the figure the surface itself just rendered, so the
 * lobby never disagrees with the screen the player has just left, and it
 * costs no extra read. An announcement without a figure asks for a re-read,
 * for a caller that only knows the balance moved.
 *
 * Presentation only: it paints what it is told and decides nothing.
 */
import { FX } from './fx.js';
import { Stardust } from './stardust.js';

const STARDUST_CHANGED_EVENT = 'moonfall:stardust-changed';

function normalizeBalance(value) {
  const numeric = typeof value === 'string' && value.trim() !== '' ? Number(value) : value;
  return Number.isSafeInteger(numeric) && numeric >= 0 ? numeric : null;
}

/* The last balance any surface painted or announced. The Foundry's own pills
   used to open on the static "0" (or nothing) until their read came back —
   a visible pop from 0 to the real figure on every entry. The lobby chip
   behind them already holds the figure from sign-in, so a surface can paint
   it on its first frame and let its authoritative read confirm. Reset when a
   sign-out repaints the chip with no figure. */
let lastKnownBalance = null;

function lastKnownStardust() {
  return lastKnownBalance;
}

/**
 * Paint one balance onto the chip; `false` when there is nothing to paint.
 * @param {HTMLElement | null | undefined} element
 * @param {unknown} value
 * @param {{ render?: (n: number) => string }} [options]
 */
function paintStardustChip(element, value, { render } = {}) {
  const balance = normalizeBalance(value);
  if (!element || balance === null) return false;
  lastKnownBalance = balance;
  /* THE BALANCE NEVER NUDGES ITS NEIGHBOURS. From a thousand up the figure is
     compact and its width varies with the value ("5K", "4.99K", "4.9K"), so a
     purchase's count animation slid the Moonlight chip and everything after it
     sideways (measured 25 September 2026: +18 px, then -7 px). In that band
     the chip reserves the widest compact figure (auth.css). */
  if (element.dataset) {
    if (balance >= 1000) element.dataset.stardustBand = 'compact';
    else delete element.dataset.stardustBand;
  }
  /* The default is the bare number: the chip's Stardust mark is drawn by CSS
     (`.user-stardust::before`), so a text glyph here would print two. */
  const format = typeof render === 'function'
    ? render
    : (Stardust?.format || ((n) => `${n}`));
  if (FX?.setCountDisplay) {
    FX.setCountDisplay(element, balance, { render: format });
  } else {
    element.dataset.countValue = String(balance);
    element.textContent = format(balance);
  }
  return true;
}

/**
 * Tell the lobby chip the authoritative balance moved. Pass the new figure
 * whenever the caller has it (a mutation result, a profile read); omit it to
 * ask the chip to re-read.
 */
function announceStardust(value) {
  if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') return;
  const balance = normalizeBalance(value);
  if (balance !== null) lastKnownBalance = balance;
  try {
    window.dispatchEvent(new CustomEvent(STARDUST_CHANGED_EVENT, {
      detail: balance === null ? {} : { stardust: balance },
    }));
  } catch (_) { /* presentation only */ }
}

/**
 * Follow the balance other surfaces announce, for a surface that keeps its
 * own figure (the Collection). `handler` receives the new balance, or `null`
 * when the announcer only knows it moved and the listener should re-read.
 * Returns the unsubscribe.
 * @param {(balance: number | null) => void} handler
 * @returns {() => void}
 */
function onStardustAnnounced(handler) {
  if (typeof window === 'undefined' || typeof handler !== 'function') return () => {};
  const listener = (event) => handler(normalizeBalance(event?.detail?.stardust));
  window.addEventListener(STARDUST_CHANGED_EVENT, listener);
  return () => window.removeEventListener(STARDUST_CHANGED_EVENT, listener);
}

/**
 * @param {HTMLElement} element
 * @param {{ initial?: number, render?: (n: number) => string, read?: () => (Promise<any>|any) }} [options]
 * @returns {{ ready: Promise<void>, refresh: () => Promise<void>, dispose: () => void }}
 */
function mountStardustChip(element, { initial, render, read } = {}) {
  let disposed = false;
  let ready = Promise.resolve();

  paintStardustChip(element, initial, { render });

  const refresh = () => {
    ready = Promise.resolve()
      .then(() => (typeof read === 'function' ? read() : null))
      .then((next) => {
        if (disposed) return;
        const balance = next && typeof next === 'object' ? next.stardust : next;
        paintStardustChip(element, balance, { render });
      })
      .catch((err) => {
        if (disposed) return;
        console.warn('[Stardust] Could not refresh the balance chip:', err);
      });
    return ready;
  };

  const onStardustChanged = (event) => {
    if (disposed) return;
    const announced = normalizeBalance(event?.detail?.stardust);
    if (announced !== null) {
      paintStardustChip(element, announced, { render });
      return;
    }
    void refresh();
  };

  if (typeof window !== 'undefined') {
    window.addEventListener(STARDUST_CHANGED_EVENT, onStardustChanged);
  }

  return {
    get ready() { return ready; },
    refresh,
    dispose: () => {
      disposed = true;
      // The chip is re-mounted per sign-in; the figure it held is nobody's
      // until the next account paints its own.
      lastKnownBalance = null;
      if (typeof window === 'undefined') return;
      window.removeEventListener(STARDUST_CHANGED_EVENT, onStardustChanged);
    },
  };
}

export {
  STARDUST_CHANGED_EVENT,
  announceStardust,
  lastKnownStardust,
  mountStardustChip,
  onStardustAnnounced,
  paintStardustChip,
};
